// imports react and next
import React, { useCallback, useEffect, useState } from "react";
import dynamic from "next/dynamic";
import { useDispatch } from "react-redux";

// graphql imports
import { TICINO_SUBPAGE_QUERY } from "@/graphql/ticinoPageQuery";
import { LABEL_AND_CONFIG_QUERY } from "@/graphql/settingsQuery";

// redux imports
import store from "@/redux/store";
import {
  fetchLabelsAndConfigData,
  setLabelsConfigsData,
} from "@/redux/actions/labelsAndConfigActions";
import { fetchCMSDataQuery } from "@/services/requestService";
import { fetchTicinoSubpageData } from "@/redux/actions/ticinoSubpageActions";

// components imports
const MetaTags = dynamic(() => import("@/common/MetaTags"));
const MainSlider = dynamic(() => import("@/components/Sliders"));
const StickyScrollBar = dynamic(() => import("@/components/StickyScrollBar"));
const AbstractCard = dynamic(() => import("@/components/Card"));
const Divider = dynamic(() => import("@/components/Divider"));
const GridSection = dynamic(() => import("@/components/Section/GridSection"));
const IntroSectionInner = dynamic(() => import("@/components/Section/IntroSectionInner"));
const MultipleImageSlider = dynamic(() => import("@/components/Sliders/MultipleImageSlider"));
const ContainerWithShadow = dynamic(() => import("@/components/Section/ContainerWithShadow"));
const Breadcrumbs = dynamic(() => import("@/components/Breadcrumbs"));

//import assets
import DividerImage1 from "@/assets/Divider1.png";

// imports utilities
import { getSelectedLangData, isEmpty } from "@/utils/functions";
import { useRouter } from "next/router";

const TicinoSubpage = (props) => {
  const { allTicinoSubpageData, ticinoSubpages, labelsConfigData, parentPageData } = props;

  const dispatch = useDispatch();
  const { query } = useRouter();

  const [labelsList, setLabelsList] = useState({});
  const [siteConfig, setSiteConfig] = useState({});
  const [subageSlides, setSubpageSlides] = useState([]);

  const initializeWidget = useCallback(() => {
    if (query?.slug === "ticino-top-ten") {
      var tttWidget = new TTTWidget();
      tttWidget.setSource("https://www.ticinotopten.ch");
      tttWidget.setId("ttt-widget-it");
      tttWidget.setLang(getSelectedLangData());
      tttWidget.setPartnerId(process.env.TICINOTOPTEN_PARTNER_ID);
      tttWidget.setWidth(800);
      // tttWidget.render();
    }
  }, [query]);

  // initialise Widget on page load
  useEffect(() => {
    initializeWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //set the other subpages to slides
  useEffect(() => {
    if (!isEmpty(ticinoSubpages)) {
      const slides = ticinoSubpages?.map((item) => {
        return {
          ...item,
          slug: `/${item?.parentSlug?.slug}/${item?.slug}`,
        };
      });
      setSubpageSlides(slides);
    }
  }, [ticinoSubpages]);

  useEffect(() => {
    // Set labelslist local state while changed on redux store data
    dispatch(setLabelsConfigsData(labelsConfigData));
    setLabelsList(labelsConfigData?.buttonsAndLabelsPage);
    setSiteConfig(labelsConfigData?.siteConfig);
  }, [dispatch, labelsConfigData]);

  return (
    <>
      <MetaTags
        metaTags={allTicinoSubpageData?._seoMetaTags}
        seoData={allTicinoSubpageData?.seo}
        noFollow={allTicinoSubpageData?.nofollow}
        noIndex={allTicinoSubpageData?.noindex}
      />
      <StickyScrollBar />
      <div className="relative w-full">
        <MainSlider sliderData={allTicinoSubpageData?.gallery} siteConfig={siteConfig} />
      </div>
      <ContainerWithShadow containerWidthClass="container">
        <Breadcrumbs
          items={[
            {
              label: "Home",
              path: "/",
            },
            {
              label: parentPageData?.title,
              path: `/${parentPageData?.slug}`,
            },
            {
              label: allTicinoSubpageData?.title,
            },
          ]}
        />
        <IntroSectionInner
          title={allTicinoSubpageData?.subtitle}
          text={allTicinoSubpageData?.description}
        />
      </ContainerWithShadow>

      {/* {query?.slug === "ticino-top-ten" && (
        <div id="ttt-widget-it" className="container my-[72px]"></div>
      )} */}
      {!isEmpty(allTicinoSubpageData?.attachments2) && (
        <>
          <GridSection
            containerWidthClass="w-full"
            containerGridClass="lg:grid-cols-3 sm:grid-cols-2 xxs:grid-cols-1 sm:mx-[40px] lg:mx-0"
            containerGridGap="gap-6"
          >
            {allTicinoSubpageData?.attachments2?.map((item, index) => (
              <AbstractCard
                key={index}
                title={item?.title}
                icon={item?.icon}
                abstract={item?.abstract}
                btnUrl={
                  item?.attachment?.url ??
                  allTicinoSubpageData.attachments2WithFallbackLocale[index]?.attachment?.url
                }
                btnLabel={labelsList?.download}
                bgColor={item?.cardColor?.hex?.toLowerCase() || "#A6C7EA"}
              />
            ))}
          </GridSection>
          <Divider imgUrl={DividerImage1} />
        </>
      )}
      {!isEmpty(subageSlides) && (
        <div className="container my-[72px] px-0">
          <MultipleImageSlider slides={subageSlides} />
        </div>
      )}
    </>
  );
};

export default TicinoSubpage;

export async function getStaticProps({ preview = false, locale = "en", params }) {
  const { slug } = params;
  let query = TICINO_SUBPAGE_QUERY;
  // query = query?.replace(/\$slug/g, slug);
  query = query?.replace(/\lang/g, locale);

  const response = await store.dispatch(fetchTicinoSubpageData(query, preview, { slug }));

  const labelsConfigData = await store.dispatch(
    fetchLabelsAndConfigData(LABEL_AND_CONFIG_QUERY, preview, { lang: locale }),
  );

  return {
    props: {
      preview,
      parentPageData: response?.ticino,
      allTicinoSubpageData: response?.allTicinoSubpages?.[0] || null,
      ticinoSubpages: response?.ticino?.ticinoSubpages || [],
      labelsConfigData: labelsConfigData || {},
    },
  };
}

export async function getStaticPaths({ preview = "false", locales }) {
  const data = await fetchCMSDataQuery(`{ allTicinoSubpages { slug } }`, preview);

  const pathsArray = [];
  data?.allTicinoSubpages.map((item) => {
    locales.map((language) => {
      pathsArray.push({ params: { slug: item?.slug }, locale: language });
    });
  });

  return {
    paths: pathsArray,
    fallback: false,
  };

  // return {
  //   paths: [{ params: { slug: "bistrot" } }],
  //   fallback: true,
  // };
}
